import { useNavigate } from "react-router-dom";
import { getSession } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import { setImpersonationWrite, stopImpersonation } from "../../auth/impersonation.js";
import { Button, Icon, useT } from "@mmm/ui";
/**
 * Persistent, app-level banner shown for the whole duration of an
 * impersonation session.
 *
 * Deliberately not dismissible: the one failure mode that matters here is
 * forgetting you are someone else and acting on their account, so the signal
 * has to survive navigation and outlast any toast. It reads from the server's
 * `getSession` rather than local state so it reflects what the API actually
 * believes about the request — if the two ever disagree, the banner tells the
 * truth.
 */
export function ImpersonationBanner() {
  const sessionQuery = useQuery(getSession, undefined);
  const session = sessionQuery.data;
  const navigate = useNavigate();
  const t = useT();

  if (!session?.impersonating) return null;

  function returnToYourself() {
    stopImpersonation();
    // Symmetric to entering impersonation: the route you're on names the
    // IMPERSONATED user's subject/chapter, which your own account doesn't own,
    // so staying put would 404 or bounce. Land on the identity-agnostic
    // dashboard instead.
    void navigate("/dashboard");
  }

  return (
    <div className="mmm-impersonation-banner" role="status" aria-live="polite">
      <Icon name="alert" className="mmm-impersonation-banner-icon" />
      <div className="mmm-impersonation-banner-body">
        <p className="mmm-impersonation-banner-title">
          {t("admin.impersonatingTitle", { name: session.actingAs.displayName })}
        </p>
        <p className="mmm-impersonation-banner-message">
          {session.canWrite
            ? t("admin.impersonatingWriteMessage", { email: session.actingAs.email })
            : t("admin.impersonatingReadOnlyMessage", { email: session.actingAs.email })}
        </p>
      </div>

      <div className="mmm-impersonation-banner-actions">
        <label className="mmm-impersonation-write-toggle">
          <input
            type="checkbox"
            className="mmm-input-accent"
            checked={session.canWrite}
            onChange={(event) => setImpersonationWrite(event.target.checked)}
          />
          <span>{t("admin.allowWrites")}</span>
        </label>
        <Button variant="primary" onClick={returnToYourself}>
          {t("admin.returnToYourself")}
        </Button>
      </div>
    </div>
  );
}
